import { useState } from 'react';
import {
  deleteProfile,
  exportJSON,
  importJSON,
  listProfiles,
  saveProfile,
  updateProfile,
} from '../lib/profiles';
import { PREFECTURES, getCurrentMinWage } from '../data/prefectures';
import { MIN_WAGES } from '../data/min-wages';
import type { Category, CustomerProfile, PlanInputs } from '../types';

interface Props {
  inputs: PlanInputs;
  customerName: string;
  customerNotes: string;
  onChangeCustomerName: (s: string) => void;
  onChangeCustomerNotes: (s: string) => void;
  onChangeCategory: (cat: Category) => void;
  onChangePrefecture: (name: string) => void;
  onChangeApplicationDate: (date: string) => void;
  onLoad: (inputs: PlanInputs, name: string, notes: string) => void;
  currentProfileId: string | null;
  onSetCurrentProfileId: (id: string | null) => void;
}

const CATEGORY_OPTIONS: { value: Category; label: string }[] = [
  { value: 'A_no', label: '区分A・加点希望なし' },
  { value: 'A_yes', label: '区分A・加点希望あり（年平均成長率 3.0%以上）' },
  { value: 'B', label: '区分B・既受給（年平均成長率 3.5%以上 必須）' },
];

const CATEGORY_LABEL: Record<Category, string> = {
  A_no: '区分A・加点希望なし',
  A_yes: '区分A・加点希望あり',
  B: '区分B・既受給',
};

const CATEGORY_NOTES: Record<Category, JSX.Element> = {
  A_no: (
    <>
      <span className="lbl">区分A・加点希望なし：</span>
      過去のIT補助金（2022〜2025年度）の<b>受給歴がなく</b>、賃金引上げ加点を希望しないケース。Step 13の表明設問は「いいえ」を選択。
      <br />
      <small>
        ※ 公募要領上の年平均成長率要件はありません。本ツール既定の自動入力（年1.0〜1.5%）は企業健全性のための任意目標です。
      </small>
    </>
  ),
  A_yes: (
    <>
      <span className="lbl">区分A・加点希望あり：</span>
      過去のIT補助金<b>受給歴なし</b>かつ加点希望。1人当たり給与支給総額 年平均成長率 <b>3.0%</b> 以上が必要。事業場内最低賃金 ≧ 地域別最低賃金 +30円（さらに +50円以上で追加加点）。賃金引上げ計画の表明と書面保管が必須。
    </>
  ),
  B: (
    <>
      <span className="lbl">区分B・既受給：</span>
      過去IT補助金（2022〜2025年度）の<b>受給歴あり</b>。1人当たり給与支給総額 年平均成長率 <b>3.5%</b> 以上が <b>申請要件として必須</b>。未達の場合、<b>補助金全額返還</b>のリスクあり。
    </>
  ),
};

export function ProfileManager({
  inputs,
  customerName,
  customerNotes,
  onChangeCustomerName,
  onChangeCustomerNotes,
  onChangeCategory,
  onChangePrefecture,
  onChangeApplicationDate,
  onLoad,
  currentProfileId,
  onSetCurrentProfileId,
}: Props) {
  const targetDate = new Date(inputs.applicationDate);
  const wage = getCurrentMinWage(inputs.prefectureName, targetDate);
  const entry = MIN_WAGES.find((p) => p.name === inputs.prefectureName);
  const showR7Warning = entry !== undefined && wage.appliedYear !== 'R7';
  const [profiles, setProfiles] = useState<CustomerProfile[]>(listProfiles);
  const [importValue, setImportValue] = useState('');
  const [importMessage, setImportMessage] = useState('');

  const refresh = () => setProfiles(listProfiles());

  const handleSave = () => {
    if (!customerName.trim()) {
      alert('顧客名を入力してください。');
      return;
    }
    if (currentProfileId) {
      updateProfile(currentProfileId, { name: customerName, inputs, notes: customerNotes });
    } else {
      const created = saveProfile(customerName, inputs, customerNotes);
      onSetCurrentProfileId(created.id);
    }
    refresh();
  };

  const handleNew = () => onSetCurrentProfileId(null);

  const handleLoad = (id: string) => {
    const p = profiles.find((x) => x.id === id);
    if (!p) return;
    onLoad(p.inputs, p.name, p.notes);
    onSetCurrentProfileId(p.id);
  };

  const handleDelete = (id: string) => {
    if (!confirm('このプロファイルを削除しますか？')) return;
    deleteProfile(id);
    if (currentProfileId === id) onSetCurrentProfileId(null);
    refresh();
  };

  const handleExport = () => {
    const json = exportJSON();
    const blob = new Blob([json], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `plan-calculator-profiles-${new Date().toISOString().slice(0, 10)}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleImport = () => {
    const result = importJSON(importValue);
    if (result.ok) {
      setImportMessage(`${result.count} 件のプロファイルをインポートしました。`);
      setImportValue('');
      refresh();
    } else {
      setImportMessage(`インポート失敗: ${result.error}`);
    }
  };

  return (
    <div className="section">
      <h2>
        <span className="num">1</span>顧客プロファイル & 基本情報
      </h2>
      <p className="section-desc">
        顧客名・申請区分・所在地などの基本情報を入力します。試算内容を顧客単位で保存・呼び出しできます（ブラウザの localStorage に保存）。
      </p>

      {/* 顧客名 */}
      <div className="form-row">
        <div className="label">顧客名</div>
        <div>
          <input
            type="text"
            value={customerName}
            onChange={(e) => onChangeCustomerName(e.target.value)}
            placeholder="例：株式会社○○○○"
            className="left-align"
          />
        </div>
      </div>

      {/* 申請区分・加点希望 */}
      <div className="form-row form-row-stack">
        <div className="label">
          申請区分・加点希望
          <small>過去のIT導入補助金（2022〜2025年度）の受給歴と、加点希望の有無で選択</small>
        </div>
        <div>
          <div className="radio-group">
            {CATEGORY_OPTIONS.map((opt) => (
              <label key={opt.value} className={inputs.category === opt.value ? 'active' : ''}>
                <input
                  type="radio"
                  name="category"
                  value={opt.value}
                  checked={inputs.category === opt.value}
                  onChange={() => onChangeCategory(opt.value)}
                />
                {opt.label}
              </label>
            ))}
          </div>
          <div className={`note note-${inputs.category}`}>{CATEGORY_NOTES[inputs.category]}</div>
        </div>
      </div>

      {/* 申請予定日 */}
      <div className="form-row">
        <div className="label">
          申請予定日
          <small>地域別最低賃金の発効済み判定に使用（既定：今日）</small>
        </div>
        <div>
          <input
            type="date"
            value={inputs.applicationDate}
            onChange={(e) => onChangeApplicationDate(e.target.value)}
          />
        </div>
      </div>

      {/* 都道府県 */}
      <div className="form-row">
        <div className="label">
          主たる事業場の所在地
          <small>選択すると申請予定日に有効な地域別最低賃金が自動セットされます</small>
        </div>
        <div>
          <select
            value={inputs.prefectureName}
            onChange={(e) => {
              const pref = PREFECTURES.find((p) => p.name === e.target.value);
              if (pref) onChangePrefecture(pref.name);
            }}
          >
            {PREFECTURES.map((p) => (
              <option key={p.name} value={p.name}>
                {p.name}（{p.regionMin.toLocaleString()}円）
              </option>
            ))}
          </select>
          <div className="section-desc" style={{ marginTop: '0.4rem', fontSize: '0.85em' }}>
            申請予定日 {inputs.applicationDate} 時点で、{inputs.prefectureName} の地域別最低賃金は{' '}
            <b>{wage.amount.toLocaleString()}円</b>（
            {wage.appliedYear === 'R7' ? '令和7年度改定後' : wage.appliedYear === 'R6' ? '令和6年度改定後' : '令和5年度改定後'}・
            {wage.effective} 発効）です。
          </div>
          {showR7Warning && entry !== undefined ? (
            <div
              className="warn-box"
              style={{ marginTop: '0.5rem', fontSize: '0.85em', padding: '0.6rem 0.8rem' }}
            >
              <span className="lbl">
                <i className="fas fa-exclamation-triangle" /> 注意：
              </span>
              {inputs.prefectureName} の令和7年度改定（発効日：{entry.R7.effective}）が申請予定日時点で未発効のため、
              現状は令和6年度の値が法定値です。申請日が令和7年度発効日以降の場合は{' '}
              <b>{entry.R7.amount.toLocaleString()}円</b> が適用されます。
            </div>
          ) : null}
        </div>
      </div>

      {/* メモ */}
      <div className="form-row">
        <div className="label">メモ</div>
        <div>
          <textarea
            value={customerNotes}
            onChange={(e) => onChangeCustomerNotes(e.target.value)}
            rows={2}
            placeholder="案件の特記事項、推奨パターンの理由など"
            className="left-align memo"
          />
        </div>
      </div>

      {/* 保存メニュー（区分・都道府県の下に配置） */}
      <div className="btn-row" style={{ marginTop: '1rem' }}>
        <button className="btn btn-purple" onClick={handleSave}>
          <i className="fas fa-save" /> {currentProfileId ? '上書き保存' : '新規保存'}
        </button>
        {currentProfileId && (
          <button className="btn btn-gray" onClick={handleNew}>
            <i className="fas fa-plus" /> 新規プロファイルとして作成
          </button>
        )}
        <button className="btn btn-gray" onClick={handleExport}>
          <i className="fas fa-download" /> JSON エクスポート
        </button>
      </div>

      {profiles.length > 0 && (
        <>
          <h3>保存済みプロファイル（{profiles.length}件）</h3>
          <div className="table-wrap">
            <table className="plan-table compact">
              <thead>
                <tr>
                  <th>顧客名</th>
                  <th>申請区分／加点</th>
                  <th>所在地</th>
                  <th>更新日</th>
                  <th>操作</th>
                </tr>
              </thead>
              <tbody>
                {profiles.map((p) => (
                  <tr key={p.id} className={p.id === currentProfileId ? 'highlight' : ''}>
                    <td>{p.name}</td>
                    <td>
                      <small>{CATEGORY_LABEL[p.inputs.category]}</small>
                    </td>
                    <td>{p.inputs.prefectureName}</td>
                    <td>
                      <small>{p.updatedAt.slice(0, 10)}</small>
                    </td>
                    <td>
                      <button className="btn btn-mini btn-purple" onClick={() => handleLoad(p.id)}>
                        読込
                      </button>{' '}
                      <button className="btn btn-mini btn-gray" onClick={() => handleDelete(p.id)}>
                        削除
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      <details style={{ marginTop: '1rem' }}>
        <summary style={{ cursor: 'pointer', fontWeight: 600, color: 'var(--g700)' }}>
          JSON インポート
        </summary>
        <textarea
          value={importValue}
          onChange={(e) => setImportValue(e.target.value)}
          rows={4}
          placeholder="エクスポート済みJSONを貼り付け"
          className="left-align json-import"
        />
        <div className="btn-row">
          <button className="btn btn-purple" onClick={handleImport} disabled={!importValue.trim()}>
            インポート（既存を上書き）
          </button>
        </div>
        {importMessage && <div className="hint">{importMessage}</div>}
      </details>
    </div>
  );
}
